import { Component, OnInit, Input, OnDestroy, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { AppState } from '../../redux';
import { QueryInfo, ThreadListConfig, SortInfo } from '../../pages/research/research.view-model';
import { DeviceDetectorService } from 'ngx-device-detector';

import { DataList } from '../../tamalelibs/models/data-list.model';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import { StoreQuerierService } from '../../services/store-querier.service';
import { NoteService } from '../../services/note.service';
import { systemCurrentContactSelector } from '../../redux/reducers/system.reducer';
import { NoteDialogService } from '../note-dialog/note-dialog.service';
import { NoteDialogOpenOptions, NoteDialogType } from '../note-dialog/note-dialog.model';
import { AlertWindowService } from '../../widgets/alert-window/alert-window.service';
import { DELETE_NOTE, DELETE_NOTES } from '../../tamalelibs/redux/note.actions';
import { AlertButton, AlertBtnTypes } from '../../widgets/alert-window/alert-window.model';
import { LONG_TOUCH_INTERVAL } from '../../tamalelibs/constants/business.constants';
import { PrintService } from '../../services/print.service';
import { ResearchScreenService } from '../../services/research-screen.service';
import { ThreadEntry } from '../../tamalelibs/models/thread-entry.model';
import { ShowAsMode } from '../display/display.model';
import { CalendarDialogOpenOptions, CalendarDialogType } from '../calendar-dialog/calendar-dialog.model';
import { CalendarDialogService } from '../calendar-dialog/calendar-dialog.service';
import { PrintActions } from '../print/print.model';
import { AppendEntitiesDialogOpenOptions, AppendEntitiesDialogType } from '../append-entities-dialog/append-entities-dialog.model';
import { AppendEntitiesDialogService } from '../append-entities-dialog/append-entities-dialog.component.service';
import { SystemUser } from '../../tamalelibs/models/user.model';

export const DEFAULT_PAGE_SIZE = 100;
export const DEFAULT_ITEM_HEIGHT = 96;

@Component({
    selector: 'snippet-thread-list',
    templateUrl: './snippet-thread-list.component.html',
    styleUrls: ['./snippet-thread-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnippetThreadListComponent implements OnInit, OnDestroy {
    @Input() config: ThreadListConfig;
    @ViewChild('listContainer', { static: false }) listContainerEle: ElementRef;

    loadingMore = false;
    reloading = false;
    selectedIndexes: Set<number> = new Set();
    selectedIndex: number;
    selectedItem: ThreadEntry;
    threadEntryList: DataList<ThreadEntry> = new DataList<ThreadEntry>();
    contextMenuConfig: ContextMenuConfig;
    isIPAD = false;
    sortInfo: SortInfo;

    private _currentPageIndex = 1;
    private _currentUser: SystemUser;
    private _destroySubscriptions: Array<Subscription> = [];
    private _isAdminUser = false;
    private _isComplianceUser = false;
    private _queryInfo: QueryInfo;
    private _selectedItems: ThreadEntry[] = [];
    private _touchstart: number;
    private _hasDeletePermissionThreadIds: Array<string>;
    private _showAsMode: ShowAsMode;

    constructor(
        private _store: Store<AppState>,
        private _deviceService: DeviceDetectorService,
        private _storeQuerier: StoreQuerierService,
        private _noteService: NoteService,
        private _noteDialogService: NoteDialogService,
        private _alertWindow: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _printService: PrintService,
        private _researchScreenService: ResearchScreenService,
        private _calendarDialogService: CalendarDialogService,
        private _appendEntitiesDialogService: AppendEntitiesDialogService,
    ) {
        this.isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
    }

    ngOnInit() {
        this._currentUser = this._storeQuerier.getCurrentUser();
        if (this._currentUser && this._currentUser.credential) {
            this._isAdminUser = this._currentUser.credential.admin;
            this._isComplianceUser = this._currentUser.credential.compliance;
        }
        this._destroySubscriptions.push(
            this._store.pipe(
                select(this.config.threadListItemSelector)
            ).subscribe(items => this._onSubscribeThreadEntryListItem(items)),
        );

        this._destroySubscriptions.push(
            this.config.actions$.subscribe(action => this._onSubscribeActions(action))
        );

        // handle re-initialize during layout change
        this._store.pipe(
            select(this.config.threadListSelector),
            take(1)
        ).subscribe(threadEntryList => {
            if (threadEntryList && threadEntryList.selectedIndex) {
                this.selectedIndexes.clear();
                this.selectedIndex = threadEntryList.selectedIndex.index;
                this.selectedItem = threadEntryList.items[this.selectedIndex];
                this.selectedIndexes.add(this.selectedIndex);
                this._ensureSelectedItemVisible(this.selectedIndex);
                this._showAsMode = this.config.showAsMode;
            }

            if (threadEntryList && threadEntryList.currentPageIndex) {
                this._currentPageIndex = threadEntryList.currentPageIndex;
            }
            this._changeDetectorRef.detectChanges();
        });

        // handle re-initialize during layout change
        this._store.pipe(
            select(this.config.queryInfoSelector),
            take(1)
        ).subscribe(queryInfo => {
            this._onSubscribeQueryInfo(queryInfo);
        });

        this._initContextMenu();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onContextMenu(event, item: ThreadEntry, index: number) {
        this._selectedItems = this._researchScreenService.getSelectedItems(this.threadEntryList, this.selectedIndexes);

        if (this.selectedIndexes.size > 1) {
            this.contextMenuConfig.items = [];
            if (!this.isIPAD) {
                this._hasDeletePermissionThreadIds = this._researchScreenService.getHasDeletePermissionThreadEntryIds(this._selectedItems);
                if (this._hasDeletePermissionThreadIds && this._hasDeletePermissionThreadIds.length > 0) {
                    this.contextMenuConfig.items.push({ id: '6', text: 'Delete notes', disabled: false });
                }
                this.contextMenuConfig.items.push({ id: '8', text: 'Print (type "p")', disabled: false });
            }
            if (this._isAdminUser && this._isComplianceUser) {
                this.contextMenuConfig.items.push({ id: '9', text: 'Append entity(s)', disabled: false });
            }
        } else {
            this._changeSelect(item, index);
            this.contextMenuConfig.items = [];
            this.contextMenuConfig.items.push({ id: '0', text: 'Open note in new tab', disabled: false });
            this.contextMenuConfig.items.push({ id: '1', text: 'Open note in new window', disabled: false });
            this.contextMenuConfig.items.push({ divider: true });
            this.contextMenuConfig.items.push({ id: '2', text: 'Add sidenote', disabled: false });
            if (item.threadEntryEditable) {
                this.contextMenuConfig.items.push({ id: '3', text: 'Edit note', disabled: false });
            }
            if (this.isIPAD) {
                if (item.threadEntryDeleable) {
                    this.contextMenuConfig.items.push({ id: '4', text: 'Copy link', disabled: false });
                }
            } else {
                this.contextMenuConfig.items.push({ id: '4', text: 'Copy link', disabled: false });
            }
            if (!this.isIPAD) {
                this.contextMenuConfig.items.push({ id: '5', text: 'Email note', disabled: false });
            }
            if (item.threadEntryDeleable) {
                this.contextMenuConfig.items.push({ id: '6', text: 'Delete note', disabled: false });
            }
            this.contextMenuConfig.items.push({ id: '7', text: 'Maximize view', disabled: false });
            if (!this.isIPAD) {
                this.contextMenuConfig.items.push({ id: '8', text: 'Print (type "p")', disabled: false });
            }
            if (this._isAdminUser && this._isComplianceUser) {
                this.contextMenuConfig.items.push({ id: '9', text: 'Append entity(s)', disabled: false });
            }
        }
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(event, item));
    }

    onItemDoubleClick(event, item: ThreadEntry) {
        // double click didn't handle any data process logic as it will trigger click first
        // and click event handler will process data
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
        }

        this.config.events$.next({
            type: 'DOUBLE_CLICK',
            payload: item
        });
    }

    onItemSelect(event, item: ThreadEntry, index: number) {
        if (this.selectedIndexes.size === 1 && this.selectedIndexes.has(index)) {
            return;
        }
        // shift first
        if (event.shiftKey) {
            let firstSelectedEntryIndex = this.selectedIndex;
            let secondSelectedEntryIndex = index;
            if (firstSelectedEntryIndex > secondSelectedEntryIndex) {
                const tempIndex = firstSelectedEntryIndex;
                firstSelectedEntryIndex = secondSelectedEntryIndex;
                secondSelectedEntryIndex = tempIndex;
            }
            let entryIndex = -1;
            for (entryIndex = firstSelectedEntryIndex; entryIndex <= secondSelectedEntryIndex; ++entryIndex) {
                this.selectedIndexes.add(entryIndex);
            }
            this.selectedItem = null;
            return;
        }

        if (event.ctrlKey || event.metaKey) { // handle command key in MAC book
            // select and de-select
            if (this.selectedIndexes.has(index)) {
                this.selectedIndexes.delete(index);
            } else {
                this.selectedIndexes.add(index);
            }
            if (this.selectedIndexes.size === 1) {
                this.selectedItem = item;
            } else {
                this.selectedItem = null;
            }
            return;
        }
        this._changeSelect(item, index);
    }

    onKeydown(event) {
        // use up/down arrow keys to select through items
        if (event.keyCode === 40) { // down
            // get the index of last selected item
            let nextIndex = 0;
            Array.from(this.selectedIndexes.values()).forEach(index => {
                if (index > nextIndex) {
                    nextIndex = index;
                }
            });
            nextIndex++;
            if (nextIndex < this.threadEntryList.length) {
                if ((nextIndex + 1) * DEFAULT_ITEM_HEIGHT >
                    (this.listContainerEle.nativeElement.clientHeight + this.listContainerEle.nativeElement.scrollTop)) {
                    // handle tha case that selected item below the visible area
                    this.listContainerEle.nativeElement.scrollTop =
                        (nextIndex + 1) * DEFAULT_ITEM_HEIGHT - this.listContainerEle.nativeElement.clientHeight;
                } else if (this.listContainerEle.nativeElement.scrollTop > (nextIndex + 1) * DEFAULT_ITEM_HEIGHT) {
                    // handle the case that selected item above the visible area
                    this.listContainerEle.nativeElement.scrollTop = nextIndex * DEFAULT_ITEM_HEIGHT;
                }

                this.selectedIndexes.clear();
                this.selectedIndex = nextIndex;
                this.selectedItem = this.threadEntryList[nextIndex];
                this.selectedIndexes.add(this.selectedIndex);
                this._store.dispatch({
                    prefix: this.config.keyInStore,
                    type: this.config.setSelectedIndexAction,
                    payload: {
                        index: nextIndex,
                        id: this.threadEntryList[nextIndex].id,
                    }
                });
            }
        } else if (event.keyCode === 38) { // up
            // get the index of first selected item
            const indexArr = Array.from(this.selectedIndexes.values());
            let nextIndex = indexArr[0];
            indexArr.forEach(index => {
                if (index < nextIndex) {
                    nextIndex = index;
                }
            });
            nextIndex--;
            if (nextIndex >= 0) {
                if ((nextIndex + 1) * DEFAULT_ITEM_HEIGHT >
                    (this.listContainerEle.nativeElement.clientHeight + this.listContainerEle.nativeElement.scrollTop)) {
                    // handle tha case that selected item below the visible area
                    this.listContainerEle.nativeElement.scrollTop =
                        (nextIndex + 1) * DEFAULT_ITEM_HEIGHT - this.listContainerEle.nativeElement.clientHeight;
                } else if (this.listContainerEle.nativeElement.scrollTop > nextIndex * DEFAULT_ITEM_HEIGHT) {
                    // handle the case that selected item above the visible area
                    this.listContainerEle.nativeElement.scrollTop = nextIndex * DEFAULT_ITEM_HEIGHT;
                }

                this.selectedIndexes.clear();
                this.selectedIndex = nextIndex;
                this.selectedItem = this.threadEntryList[nextIndex];
                this.selectedIndexes.add(this.selectedIndex);
                this._store.dispatch({
                    prefix: this.config.keyInStore,
                    type: this.config.setSelectedIndexAction,
                    payload: {
                        index: nextIndex,
                        id: this.threadEntryList[nextIndex].id,
                    }
                });
            }
        }
    }

    onScrolled() {
        this.loadingMore = true;
        this._currentPageIndex++;
        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.loadMoreAction,
            payload: {
                pageIndex: this._currentPageIndex,
                pageSize: DEFAULT_PAGE_SIZE,
                showAsMode: this._showAsMode,
                ...this._queryInfo
            }
        });
    }

    showNoData() {
        if (this.threadEntryList && this.threadEntryList.length === 0 && !this.reloading) {
            return 'flex';
        } else {
            return 'none';
        }
    }

    // For iPad
    touchstart() {
        this._touchstart = new Date().getTime();
    }

    // For iPad
    touchend(event, item: ThreadEntry, index: number) {
        if (this.isIPAD && new Date().getTime() - this._touchstart > LONG_TOUCH_INTERVAL) {
            event.clientX = event.pageX;
            event.clientY = event.pageY;
            this.onContextMenu(event, item, index);
        } else {
            this.onItemSelect(event, item, index);
        }
    }

    trackByFn(index, item) {
        return item.id;
    }

    private _appendEntities() {
        const _noteOption = new AppendEntitiesDialogOpenOptions();
        _noteOption.minimizable = true;
        _noteOption.selectedItems = this._selectedItems;
        _noteOption.openType = AppendEntitiesDialogType.AppendEntities;
        this._appendEntitiesDialogService.dialogOpen$.next(_noteOption);
    }

    private _changeSelect(item: ThreadEntry, index: number) {
        this.selectedIndexes.clear();
        this.selectedIndex = index;
        this.selectedItem = item;
        this.selectedIndexes.add(this.selectedIndex);
        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.setSelectedIndexAction,
            payload: {
                index: this.selectedIndex,
                id: item.id,
            }
        });
    }

    private _ensureSelectedItemVisible(index) {
        setTimeout(() => {
            const scrollTop = index * DEFAULT_ITEM_HEIGHT;
            this.listContainerEle.nativeElement.scrollTop = scrollTop;
        });
    }

    private _copyLink(threadEntry) {
        const entryClass = threadEntry.parentEntry.entryClass;
        this._noteService.shareNote(threadEntry.threadEntryThreadId, entryClass);
    }

    private _editEvent(data) {
        const _noteOption = new CalendarDialogOpenOptions();
        _noteOption.openType = CalendarDialogType.EditEvent;
        _noteOption.entryId = data.threadEntryThreadId;
        _noteOption.isPluginInEditEventMode = false;
        _noteOption.minimizable = true;
        this._calendarDialogService.dialogOpen$.next(_noteOption);
    }

    private _editNote(threadEntry) {
        const _noteOption = new NoteDialogOpenOptions();
        _noteOption.entryId = threadEntry.id;
        if (threadEntry.threadEntryThreadPosition > 0) {
            _noteOption.openType = NoteDialogType.EditSideNote;
        } else {
            _noteOption.openType = NoteDialogType.EditNote;
        }
        _noteOption.minimizable = true;
        _noteOption.entityFieldEditable = this._isEditable(threadEntry);
        this._noteDialogService.dialogOpen$.next(_noteOption);
    }

    private _addSideNote(threadEntry) {
        const _noteOption = new NoteDialogOpenOptions();
        const sidenoteInfo = this._noteService.getSidenoteInfoBySettingRule(threadEntry.notes, threadEntry.threadEntryTotalNotesInThread);
        if (sidenoteInfo) {
            _noteOption.entryId = sidenoteInfo.entryId;
            _noteOption.entryTypeId = sidenoteInfo.entryTypeId;
        } else {
            _noteOption.entryId = threadEntry.threadEntryThreadId;
        }
        _noteOption.openType = NoteDialogType.AddSideNote;
        _noteOption.minimizable = true;
        _noteOption.entityFieldEditable = this._isEditable(threadEntry);
        this._noteDialogService.dialogOpen$.next(_noteOption);
    }

    private _isEditable(threadEntry) {
        const flag = threadEntry.notes.find(note => {
            return note.editable === false;
        });
        if (flag) {
            return false;
        } else {
            return true;
        }
    }

    private _initContextMenu() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.stateful = false;
        this.contextMenuConfig.forceRefresh = true;
        this._destroySubscriptions.push(this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event)));
        this._changeDetectorRef.detectChanges();
    }

    private _onMenuSelect(event) {
        const type = +event.payload.id;
        const threadEntry = event.payload.data;
        switch (type) {
            case 0:
                this._openNoteInNewTab(threadEntry);
                break;
            case 1:
                this._openNoteInNewWindow(threadEntry);
                break;
            case 2:
                this._addSideNote(threadEntry);
                break;
            case 3:
                if (threadEntry.threadEntryEntryClass === 0) {
                    // Edit Event
                    this._editEvent(threadEntry);
                } else {
                    // Edit Note
                    this._editNote(threadEntry);
                }
                break;
            case 4:
                // Copy Link
                this._copyLink(threadEntry);
                break;
            case 5:
                // Email Note
                this._shareNote(threadEntry);
                break;
            case 6:
                // Delete
                if (this._hasDeletePermissionThreadIds && this._hasDeletePermissionThreadIds.length > 0) {
                    this._deleteNotes(this._hasDeletePermissionThreadIds);
                    this._hasDeletePermissionThreadIds = [];
                } else {
                    this._delete(threadEntry);
                }
                break;
            case 7:
                this._triggerFullView(threadEntry);
                break;
            case 8:
                // Print
                this._printNotes();
                break;
            case 9:
                this._appendEntities();
                break;
            default:
                break;
        }
        this._changeDetectorRef.detectChanges();
    }

    private _openNoteInNewTab(threadEntry) {
        const noteLink = this._noteService.getShareNoteLink(threadEntry.threadEntryThreadId, threadEntry.threadEntryEntryClass);
        window.open(noteLink, '_blank');
    }

    private _openNoteInNewWindow(threadEntry) {
        const marginLeft = (window.screen.availWidth - 800) / 2;
        const marginTop = (window.screen.availHeight - 800) / 2;
        const noteLink = this._noteService.getShareNoteLink(threadEntry.threadEntryThreadId, threadEntry.threadEntryEntryClass);
        window.open(noteLink, '_blank', 'width=800,height=800,top=' + marginTop + ',left=' + marginLeft);
    }

    /**
     *  print selected thread list.
     *
     * @private
     * @param {*} threadList
     * @memberof TopThreadListComponent
     */
    private _printNotes(): void {
        this._printService.printThreads(this.threadEntryList, this.selectedIndexes);
    }

    private _delete(threadEntry) {
        const content: string[] = [];
        if (this._showAsMode === 0 && threadEntry.threadEntryThreadPosition !== 0) {
            content.push('Are you sure you want to delete this sidenote?');
            content.push('Any attachments will also be deleted.');
        } else {
            content.push('Are you sure you want to delete this note?');
            content.push('Any attachments or sidenotes will also be deleted.');
        }
        const cancelBtn: AlertButton = {
            text: 'Cancel',
            type: AlertBtnTypes.tertiary
        };
        const confirmButton: AlertButton = {
            text: 'Delete',
            type: AlertBtnTypes.destructive
        };
        const subscription = this._alertWindow.
            custom('Delete confirmation required', content, cancelBtn, confirmButton)
            .subscribe((result: boolean) => {
                if (result) {
                    this._store.dispatch({
                        type: DELETE_NOTE,
                        payload: {
                            id: threadEntry.id,
                            componentId: 'snippet thread list'
                        }
                    });
                }
                this._changeDetectorRef.detectChanges();
                subscription.unsubscribe();
            });
    }

    private _deleteNotes(deletethreadEntryIds) {
        const content: string[] = [];
        content.push('Are you sure you want to delete these notes?');
        content.push('Any attachments or sidenotes will also be deleted.');
        const cancelBtn: AlertButton = {
            text: 'Cancel',
            type: AlertBtnTypes.tertiary
        };
        const confirmButton: AlertButton = {
            text: 'Delete',
            type: AlertBtnTypes.destructive
        };
        const subscription = this._alertWindow.
            custom('Delete confirmation required', content, cancelBtn, confirmButton)
            .subscribe((result: boolean) => {
                if (result) {
                    this._store.dispatch({
                        type: DELETE_NOTES,
                        payload: {
                            ids: deletethreadEntryIds,
                            componentId: 'snippet thread list'
                        }
                    });
                }
                this._changeDetectorRef.detectChanges();
                subscription.unsubscribe();
            });
    }

    private _onSubscribeActions(action: any) {
        if (action.type === 'RELOAD') {
            this._reload(action.payload);
        } else if (action.type === 'FOCUS') {
            this.listContainerEle.nativeElement.parentElement.focus();
        } else if (action.type === PrintActions.PRINT) {
            this._printNotes();
        }
        this._changeDetectorRef.detectChanges();
    }

    private _onSubscribeQueryInfo(queryInfo) {
        this._queryInfo = queryInfo;
        this.sortInfo = {
            sortBy: queryInfo.sortBy,
            sortOrder: queryInfo.sortOrder
        };
        this._changeDetectorRef.detectChanges();
    }

    private _onSubscribeThreadEntryListItem(items: DataList<ThreadEntry>) {
        this.threadEntryList = items;
        if (this.reloading) {
            this.reloading = false;
            // focus on DOM to let use could directly use key up/down to switch thread
            this.listContainerEle.nativeElement.parentElement.focus();
            if (this.threadEntryList && this.threadEntryList.length > 0) {
                // need to select the first item by default after reload
                this.selectedIndex = 0;
                this.selectedItem = this.threadEntryList[0];
                this.selectedIndexes.clear();
                this.selectedIndexes.add(this.selectedIndex);
            }
        }

        if (this.loadingMore) {
            this.loadingMore = false;
        }
        this._changeDetectorRef.detectChanges();
    }

    private _reload(payload) {
        const queryInfo = payload.queryInfo;
        this._showAsMode = payload.showAsMode;
        this.reloading = true;
        this.listContainerEle.nativeElement.scrollTop = 0;
        this._currentPageIndex = 1;
        this.selectedIndexes.clear();
        this.selectedIndex = null;
        this.selectedItem = null;
        this._queryInfo = queryInfo;
        // set value for sortInfo
        this.sortInfo = {
            sortBy: queryInfo.sortBy,
            sortOrder: queryInfo.sortOrder
        };
        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.setQueryInfoAction,
            payload: this._queryInfo
        });
        this._store.dispatch({
            prefix: this.config.keyInStore,
            type: this.config.reloadAction,
            payload: {
                pageIndex: 1,
                pageSize: DEFAULT_PAGE_SIZE,
                showAsMode: this._showAsMode,
                ...this._queryInfo
            }
        });
    }

    private _shareNote(threadEntry) {
        const entryClass = threadEntry.parentEntry.entryClass;
        const contact = this._storeQuerier.queryBySelector(systemCurrentContactSelector);
        const noteLink = this._noteService.getShareNoteLink(threadEntry.threadEntryThreadId, entryClass);
        const emailBody = this._noteService.getShareNoteEmailBody(contact.name, threadEntry.parentEntry.subject, noteLink);
        this._noteService.emailNote(threadEntry.parentEntry.calculatedSubject, emailBody);
        this._changeDetectorRef.detectChanges();
    }

    private _triggerFullView(threadEntry) {
        this.config.events$.next({
            type: 'MAXIMIZE',
            payload: threadEntry
        });
    }
}
